import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useSearchIcd10Codes, type Icd10Code } from "@/clinical-codes/useSearchIcd10Codes";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { useState } from "react";
import type { ExpectedIcd10Code } from "@/test-data/useMedicalTextCodes";

// Configuration for how to access and display code data
export interface CodeAccessorConfig<T> {
  // How to get the code string from the item
  getCode: (item: T) => string;
  // How to get the description from the item
  getDescription: (item: T) => string;
  // How to get the code ID for comparison
  getCodeId: (item: T) => string;
  // How to create a new item from an Icd10Code
  createFromCode: (code: Icd10Code, existingItems: T[]) => T;
  // How to update the order index of items
  updateOrderIndex?: (items: T[]) => T[];
  // Optional: custom display component
  renderItem?: (item: T, index: number, isOptional: boolean) => React.ReactNode;
}

interface DiagnosesSectionProps<T = ExpectedIcd10Code> {
  selectedDiagnoses: T[];
  onDiagnosesChange: (diagnoses: T[]) => void;
  onToggleOptional: (id: string) => void;
  onFormChange: () => void;
  // Configuration for how to work with the data
  codeAccessor: CodeAccessorConfig<T>;
  // Optional: custom label
  label?: string;
  // Optional: show primary indicator
  showPrimaryIndicator?: boolean;
  // Optional: custom placeholder
  searchPlaceholder?: string;
  // Optional: disable drag and drop
  disableDragDrop?: boolean;
}

export function DiagnosesSection<T extends { id: string; is_optional: boolean } = ExpectedIcd10Code>({
  selectedDiagnoses,
  onDiagnosesChange,
  onToggleOptional,
  onFormChange,
  codeAccessor,
  label = "Diagnoses (ICD-10)",
  showPrimaryIndicator = true,
  searchPlaceholder = "Search diagnoses...",
  disableDragDrop = false,
}: DiagnosesSectionProps<T>) {
  const [diagnosesSearchTerm, setDiagnosesSearchTerm] = useState("");

  const { codes: diagnosisCodes } = useSearchIcd10Codes(diagnosesSearchTerm);

  const addDiagnosis = (code: Icd10Code) => {
    const existingCodeId = code.id;
    if (!selectedDiagnoses.find((item) => codeAccessor.getCodeId(item) === existingCodeId)) {
      const newItem = codeAccessor.createFromCode(code, selectedDiagnoses);
      const updatedItems = [...selectedDiagnoses, newItem];
      const finalItems = codeAccessor.updateOrderIndex ? codeAccessor.updateOrderIndex(updatedItems) : updatedItems;
      onDiagnosesChange(finalItems);
      onFormChange();
    }
  };

  const removeDiagnosis = (_id: string, index: number) => {
    const newDiagnoses = [...selectedDiagnoses];
    newDiagnoses.splice(index, 1);
    const finalItems = codeAccessor.updateOrderIndex ? codeAccessor.updateOrderIndex(newDiagnoses) : newDiagnoses;
    onDiagnosesChange(finalItems);
    onFormChange();
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || disableDragDrop) return;
    const { source, destination } = result;
    const items = Array.from(selectedDiagnoses);
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);
    const finalItems = codeAccessor.updateOrderIndex ? codeAccessor.updateOrderIndex(items) : items;
    onDiagnosesChange(finalItems);
    onFormChange();
  };

  // Default render function for items
  const renderDefaultItem = (item: T, index: number, isOptional: boolean) => (
    <div className="flex items-start justify-start gap-2 flex-1">
      <span>
        {codeAccessor.getCode(item)} - {codeAccessor.getDescription(item)}
        {isOptional && <span className="text-green-600 text-sm ml-2">(Optional)</span>}
      </span>
      {showPrimaryIndicator && index === 0 ? <span className="text-xs text-gray-500">[primary]</span> : null}
    </div>
  );

  const renderItem = codeAccessor.renderItem || renderDefaultItem;

  return (
    <div className="space-y-2 w-full">
      <Label>{label}</Label>
      <div className="w-full px-1 mb-2 relative">
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          placeholder={searchPlaceholder}
          value={diagnosesSearchTerm}
          onChange={(e) => setDiagnosesSearchTerm(e.target.value)}
        />
        {diagnosesSearchTerm && (
          <div className="absolute left-0 right-0 bg-white border rounded shadow mt-1 max-h-40 overflow-y-auto z-50">
            {diagnosisCodes
              ?.filter(
                (code) =>
                  code.code.toLowerCase().includes(diagnosesSearchTerm.toLowerCase()) ||
                  code.description.toLowerCase().includes(diagnosesSearchTerm.toLowerCase()),
              )
              .slice(0, 10)
              .map((code) => (
                <div
                  key={code.id}
                  className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    addDiagnosis(code);
                    setDiagnosesSearchTerm("");
                  }}
                >
                  {code.code} - {code.description}
                </div>
              ))}
          </div>
        )}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="diagnoses-droppable" type="diagnoses">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="bg-gray-50 rounded border min-h-[40px] p-2"
            >
              {selectedDiagnoses.map((item, idx) => (
                <Draggable
                  key={`${item.id}-${idx}`}
                  draggableId={`${item.id}-${idx}`}
                  index={idx}
                  isDragDisabled={disableDragDrop}
                >
                  {(provided, snapshot) => (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`flex items-center justify-between p-3 mb-1 rounded shadow-sm ${
                        snapshot.isDragging ? "ring-2 ring-blue-400" : ""
                      } ${item.is_optional ? "bg-green-50" : "bg-white"}`}
                    >
                      {!disableDragDrop && (
                        <span {...provided.dragHandleProps} className="cursor-move mr-2">
                          ☰
                        </span>
                      )}
                      {renderItem(item, idx, item.is_optional)}
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <Label htmlFor={`diag-optional-${idx}`} className="text-xs">
                            Optional
                          </Label>
                          <Switch
                            id={`diag-optional-${idx}`}
                            checked={item.is_optional}
                            onCheckedChange={() => onToggleOptional(item.id)}
                          />
                        </div>
                        <button
                          type="button"
                          className="ml-2 text-red-500 hover:text-red-700"
                          onClick={() => removeDiagnosis(item.id, idx)}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}

// Helper function to create the default code accessor for ExpectedIcd10Code
export function createDefaultCodeAccessor(): CodeAccessorConfig<ExpectedIcd10Code> {
  return {
    getCode: (item) => item.icd_10_code.code,
    getDescription: (item) => item.icd_10_code.description,
    getCodeId: (item) => item.icd_10_code.id,
    createFromCode: (code, existingItems) => ({
      id: crypto.randomUUID(),
      code_id: code.id,
      is_optional: false,
      medical_text_example_id: "to_be_replaced",
      order_index: existingItems.length,
      icd_10_code: code,
    }),
    updateOrderIndex: (items) => items.map((item, index) => ({ ...item, order_index: index })),
  };
}

// Backward compatibility: export the original component with default configuration
export function DiagnosesSectionLegacy({
  selectedDiagnoses,
  onDiagnosesChange,
  onToggleOptional,
  onFormChange,
}: {
  selectedDiagnoses: ExpectedIcd10Code[];
  onDiagnosesChange: (diagnoses: ExpectedIcd10Code[]) => void;
  onToggleOptional: (id: string) => void;
  onFormChange: () => void;
}) {
  return (
    <DiagnosesSection
      selectedDiagnoses={selectedDiagnoses}
      onDiagnosesChange={onDiagnosesChange}
      onToggleOptional={onToggleOptional}
      onFormChange={onFormChange}
      codeAccessor={createDefaultCodeAccessor()}
    />
  );
}
